// ActivityFeedRow — linha do feed da Tela Atividade (wireframe seção 06,
// HTML bruto: `.arow.click > .adot + .atx + .atime`). Correção UX v2 §5.5:
// anatomia/classe/cores VERBATIM do wireframe via wireframe.css (base W0:
// .wf-feed-row/.wf-feed-dot/.wf-feed-text/.wf-feed-time) + wf-activity.css.
//
// Forma do texto segue o mockup:
//   skill     → <b>skill <code>analise-conteudo</code></b> concluída
//   agent     → <b>agent <code>morgan</code></b> finalizou execução
//   companion → <b>companion <code>jose</code></b> turno concluído
//   tool      → <b>tool <code>github_action</code></b> concluído
//   handler   → <b>pipeline-runner</b> avançou <code>target</code>
// Clique abre a modal de detalhes (gerenciada pela MCActivityPage via store).

import '@/styles/wf-activity.css';
import { cn } from '@/lib/utils';
import {
  type ActivityItem,
  type ActivityStatus,
  formatActorLead,
  relativeTime,
  summarizeActivityError,
} from '@/lib/activity';

// Dots do wireframe: default (accent) = em execução · ok · warn · bad.
// `--muted` é extensão p/ cancelled (wireframe não tem o caso).
const DOT_CLASS: Record<ActivityStatus, string | null> = {
  running: null, // .adot sem modificador = accent (wireframe)
  completed: 'wf-feed-dot--ok',
  failed: 'wf-feed-dot--bad',
  timeout: 'wf-feed-dot--warn',
  partial: 'wf-feed-dot--warn',
  cancelled: 'wf-feed-dot--muted',
};

interface ActivityFeedRowProps {
  item: ActivityItem;
  onClick?: (item: ActivityItem) => void;
}

export function ActivityFeedRow({ item, onClick }: ActivityFeedRowProps) {
  // F3 2026-07-19 (mockup aprovado pelo dono): "Nome executou como agente" /
  // "@runner executou a tool X" — formatador ÚNICO (lib/activity.formatActorLead).
  // Linhas, granularidade e clique inalterados; só o NOME de quem executou.
  const lead = formatActorLead(item);
  const ariaName = lead.display;
  // Resumo LEGÍVEL do erro (não o JSON/stack cru que vazava inline). O dump
  // completo continua no detalhe (modal) via error_message.
  const errorMsg = item.status === 'failed' ? summarizeActivityError(item.error_message) : null;

  return (
    <button
      type="button"
      onClick={() => onClick?.(item)}
      className="wf-feed-row wf-feed-row--click wf-feed-row--btn"
      aria-label={`Detalhes da execução de ${ariaName}`}
    >
      <span
        className={cn('wf-feed-dot', DOT_CLASS[item.status])}
        aria-hidden="true"
      />
      <span className="wf-feed-text">
        <b>
          {lead.display}
          {lead.code && (
            <>
              {' '}
              <code>{lead.code}</code>
            </>
          )}
        </b>{' '}
        {lead.verb}
        {lead.target && (
          <>
            {' '}
            <code>{lead.target}</code>
          </>
        )}
        {errorMsg && <span className="wf-feed-err"> — {errorMsg}</span>}
      </span>
      <span className="wf-feed-time">{relativeTime(item.started_at)}</span>
    </button>
  );
}
